import {
  DragDropContext,
  Droppable,
  Draggable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import { useDataProvider, useListContext } from "ra-core";
import { useEffect, useState, useMemo } from "react";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact } from "../types";
import { ContactCardContent } from "./ContactCard";
import { getOrderedPipelineStages } from "./stages";

type LaneDimension = "none" | "status" | "role" | "type";

interface LaneConfig {
  value: string;
  label: string;
}

// Encode a droppable cell id from stage + lane
const encodeCellId = (stage: string, lane: string) => `${stage}___${lane}`;
const decodeCellId = (id: string) => {
  const sep = id.lastIndexOf("___");
  return { stage: id.slice(0, sep), lane: id.slice(sep + 3) };
};

// Derive lane configs based on selected dimension + contacts present
function getLaneConfigs(
  dimension: LaneDimension,
  _contacts: Contact[],
): LaneConfig[] {
  if (dimension === "none") return [{ value: "__all__", label: "" }];

  if (dimension === "status") {
    return [
      { value: "A", label: "A - Strong (Pick up phone)" },
      { value: "B", label: "B - Medium (Recognize name)" },
      { value: "C", label: "C - Weak (Peripheral/Unknown)" },
      { value: "__none__", label: "No Status" },
    ];
  }

  if (dimension === "role") {
    return [
      { value: "1", label: "1 — Decision Maker" },
      { value: "2", label: "2 — Influencer" },
      { value: "3", label: "3 — Neither" },
      { value: "__none__", label: "No Influence" },
    ];
  }

  if (dimension === "type") {
    return [
      { value: "Customer", label: "Customer" },
      { value: "Ecosystem", label: "Ecosystem" },
      { value: "Partner", label: "Partner" },
      { value: "Prospect", label: "Prospect" },
      { value: "Referral Source", label: "Referral Source" },
      { value: "Student", label: "Student" },
      { value: "Vendor", label: "Vendor" },
      { value: "Other", label: "Other" },
      { value: "__none__", label: "No Type" },
    ];
  }

  return [{ value: "__all__", label: "" }];
}

// Map a contact to its lane value for the given dimension
function getLaneValue(contact: Contact, dimension: LaneDimension): string {
  if (dimension === "none") return "__all__";
  if (dimension === "status") return contact.status || "__none__";
  if (dimension === "role") return contact.influence || "__none__";
  if (dimension === "type") return contact.relationship_motion || "__none__";
  return "__all__";
}

// Build a field patch for moving to a new lane value
function getLanePatch(
  dimension: LaneDimension,
  laneValue: string,
): Partial<Contact> {
  if (dimension === "status")
    return {
      status: laneValue === "__none__" ? null : (laneValue as any),
    };
  if (dimension === "role")
    return {
      influence: laneValue === "__none__" ? (null as any) : laneValue,
    };
  if (dimension === "type")
    return {
      relationship_motion: laneValue === "__none__" ? (null as any) : laneValue,
    };
  return {};
}

export const ContactKanbanContent = () => {
  const { connectionPipelineStages } = useConfigurationContext();
  const {
    data: unorderedContacts = [],
    isPending,
    refetch,
  } = useListContext<Contact>();
  const dataProvider = useDataProvider() as any;

  const [laneDimension, setLaneDimension] = useState<LaneDimension>("none");

  // Derive columns from pipeline stages (always the x-axis)
  const columns = useMemo(() => {
    const activeStages = Array.from(
      new Set(
        unorderedContacts
          .map((c) => c.connection_pipeline_stage)
          .filter((s): s is string => !!s),
      ),
    );
    const orderedStages = getOrderedPipelineStages(
      connectionPipelineStages,
      activeStages,
    );
    if (orderedStages.length === 0) {
      orderedStages.push({
        value: "Comment/React",
        label: "Comment/React",
        order: 1,
      });
    }
    return orderedStages;
  }, [connectionPipelineStages, unorderedContacts]);

  // Derive lane rows
  const lanes = useMemo(
    () => getLaneConfigs(laneDimension, unorderedContacts),
    [laneDimension, unorderedContacts],
  );

  // contacts indexed by cell id (stage___lane)
  type CellMap = Record<string, Contact[]>;
  const [contactsByCell, setContactsByCell] = useState<CellMap>({});

  useEffect(() => {
    if (!unorderedContacts) return;
    const map: CellMap = {};
    columns.forEach((col) => {
      lanes.forEach((lane) => {
        map[encodeCellId(col.value, lane.value)] = [];
      });
    });

    unorderedContacts.forEach((contact) => {
      const stage = contact.connection_pipeline_stage || columns[0].value;
      const lane = getLaneValue(contact, laneDimension);
      const cellId = encodeCellId(stage, lane);
      if (map[cellId] !== undefined) {
        map[cellId].push(contact);
      } else {
        // fallback: first column, first lane
        map[encodeCellId(columns[0].value, lanes[0].value)].push(contact);
      }
    });

    // Sort each cell by index
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => (a.index || 0) - (b.index || 0));
    });

    setContactsByCell(map);
  }, [unorderedContacts, columns, lanes, laneDimension]);

  if (isPending) return null;

  const onDragEnd: OnDragEndResponder = async (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    const srcCellId = source.droppableId;
    const destCellId = destination.droppableId;
    const { stage: destStage, lane: destLane } = decodeCellId(destCellId);

    const draggedContact = (contactsByCell[srcCellId] || [])[source.index];
    if (!draggedContact) return;

    const newMap = { ...contactsByCell };

    // Remove from source
    const srcList = [...(newMap[srcCellId] || [])];
    srcList.splice(source.index, 1);
    newMap[srcCellId] = srcList.map((c, i) => ({ ...c, index: i }));

    // Build updated contact with new stage + lane
    const updatedContact: Contact = {
      ...draggedContact,
      connection_pipeline_stage: destStage,
      ...getLanePatch(laneDimension, destLane),
    };

    // Insert into destination
    if (srcCellId === destCellId) {
      const sameList = [...(contactsByCell[srcCellId] || [])];
      sameList.splice(source.index, 1);
      sameList.splice(destination.index, 0, updatedContact);
      newMap[srcCellId] = sameList.map((c, i) => ({ ...c, index: i }));
    } else {
      const destList = [...(newMap[destCellId] || [])];
      destList.splice(destination.index, 0, updatedContact);
      newMap[destCellId] = destList.map((c, i) => ({ ...c, index: i }));
    }

    setContactsByCell(newMap);

    // Persist changes
    const updates: (() => Promise<any>)[] = [];

    const findChanges = (cellId: string) => {
      const { stage: cellStage, lane: cellLane } = decodeCellId(cellId);
      const oldCell = contactsByCell[cellId] || [];
      const newCell = newMap[cellId] || [];
      newCell.forEach((newC) => {
        const oldC = oldCell.find((c) => c.id === newC.id);
        const hasChange =
          !oldC ||
          oldC.connection_pipeline_stage !== newC.connection_pipeline_stage ||
          getLaneValue(oldC, laneDimension) !==
            getLaneValue(newC, laneDimension) ||
          oldC.index !== newC.index;
        if (hasChange) {
          updates.push(() =>
            dataProvider.updateContactQuietly(newC.id, {
              connection_pipeline_stage: cellStage,
              ...getLanePatch(laneDimension, cellLane),
              index: newC.index,
            }),
          );
        }
      });
    };

    findChanges(srcCellId);
    if (srcCellId !== destCellId) findChanges(destCellId);

    try {
      // Execute updates sequentially to avoid network congestion and database lock contention
      for (const runUpdate of updates) {
        await runUpdate();
      }
    } catch (err) {
      console.error("Failed to persist reordering:", err);
    }
    refetch();
  };

  const showLanes = laneDimension !== "none";

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Toolbar */}
      <div className="flex justify-between items-center bg-muted/40 p-3.5 rounded-xl border border-muted-foreground/10 mb-2">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-muted-foreground">
            Swimming lanes:
          </span>
          <select
            value={laneDimension}
            onChange={(e) => setLaneDimension(e.target.value as LaneDimension)}
            className="text-sm bg-background border border-input rounded-lg px-3 py-1.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer hover:bg-muted/30 transition-colors"
          >
            <option value="none">None (all contacts)</option>
            <option value="status">Status</option>
            <option value="role">Influence (Purchasing Power)</option>
            <option value="type">Relationship Type</option>
          </select>
        </div>
        <div className="text-xs text-muted-foreground font-medium hidden sm:block">
          Columns = pipeline stages · Lanes ={" "}
          {laneDimension === "none" ? "off" : laneDimension}
        </div>
      </div>

      {/* Board */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="overflow-x-auto pb-4 [transform:rotateX(180deg)]">
          <div className="[transform:rotateX(180deg)]">
            {/* Column headers */}
            <div className="flex gap-0 min-w-max border-b border-muted-foreground/10 mb-3 pb-2">
              {showLanes && (
                <div className="w-32 shrink-0 bg-background z-20 sticky left-0" />
              )}
              {columns.map((col, colIdx) => (
                <div
                  key={col.value}
                  className={`w-56 min-w-[220px] flex-1 px-2 pb-2 ${
                    colIdx < columns.length - 1
                      ? "border-r border-muted-foreground/10"
                      : ""
                  }`}
                >
                  <div className="flex flex-col items-center py-2">
                    <h3 className="text-sm font-semibold truncate">
                      {col.label}
                    </h3>
                    <span className="text-xs text-muted-foreground font-medium">
                      {lanes.reduce(
                        (sum, lane) =>
                          sum +
                          (contactsByCell[encodeCellId(col.value, lane.value)]
                            ?.length || 0),
                        0,
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Rows = lanes */}
            {lanes.map((lane, laneIdx) => (
              <div
                key={lane.value}
                className={`flex gap-0 min-w-max py-4 ${
                  showLanes && laneIdx < lanes.length - 1
                    ? "border-b border-dotted border-muted-foreground/20"
                    : ""
                }`}
              >
                {/* Lane label on the left */}
                {showLanes && (
                  <div className="w-32 shrink-0 flex items-start justify-end pr-3 sticky left-0 bg-background z-10">
                    <span
                      className="text-xs font-semibold text-muted-foreground text-right leading-tight bg-muted/40 rounded px-2 py-1 border border-muted-foreground/10"
                      title={lane.label}
                    >
                      {lane.label}
                    </span>
                  </div>
                )}

                {/* Cells = one per stage */}
                {columns.map((col, colIdx) => {
                  const cellId = encodeCellId(col.value, lane.value);
                  const cellContacts = contactsByCell[cellId] || [];

                  return (
                    <div
                      key={cellId}
                      className={`w-56 min-w-[220px] flex-1 px-2 ${
                        colIdx < columns.length - 1
                          ? "border-r border-muted-foreground/10"
                          : ""
                      }`}
                    >
                      <Droppable droppableId={cellId}>
                        {(droppableProvided, snapshot) => (
                          <div
                            ref={droppableProvided.innerRef}
                            {...droppableProvided.droppableProps}
                            className={`flex flex-col rounded-xl gap-2 min-h-[80px] p-1 transition-colors ${
                              snapshot.isDraggingOver
                                ? "bg-primary/5 border border-dashed border-primary/30"
                                : showLanes
                                  ? "border border-transparent"
                                  : ""
                            }`}
                          >
                            {col.value !== "Archived" &&
                              cellContacts.map((contact, index) => (
                                <Draggable
                                  key={contact.id}
                                  draggableId={String(contact.id)}
                                  index={index}
                                >
                                  {(provided, snap) => (
                                    <ContactCardContent
                                      provided={provided}
                                      snapshot={snap}
                                      contact={contact}
                                    />
                                  )}
                                </Draggable>
                              ))}
                            {col.value === "Archived" && (
                              <div className="flex flex-col items-center justify-center h-full text-xs text-muted-foreground/30 border border-dashed border-muted-foreground/10 rounded-lg py-6 select-none">
                                <span>Drop to Archive</span>
                              </div>
                            )}
                            {droppableProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};
